import {
  ChatMessageBlockType,
  SalesfloorChatContentMessage,
  SalesfloorChatMessageArgs,
  SalesfloorChatMessageType,
} from 'sb-lib/types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import { registerImage } from 'shared/store/shared-actions';
import {
  chatImageUploadDataUrlChanged,
  chatImageUploadErrorChanged,
  chatImageUploadIdChanged,
  chatImageUploadProgressChanged,
  sendChatMessage,
} from 'shared/store/chat-slice';
import { ChangeEvent, useCallback, useRef, useState, DragEvent } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { addConversationMessage } from 'shared/store/salesfloor-slice';
import { selectActivePublicUser, selectActiveUser } from 'shared/store/shared-selectors';

const MAX_IMG_SIZE = 10000000;

export const useImageUpload = (
  visitorId: string,
  conversationId: number | string,
  mode = 'session',
): [
  React.MutableRefObject<HTMLDivElement | null>,
  boolean,
  (e: DragEvent<HTMLDivElement>) => void,
  (e: DragEvent<HTMLDivElement>) => void,
  (e: DragEvent<HTMLDivElement>) => void,
  (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>,
] => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dispatch = useAppDispatch();
  const activeUser = useAppSelector(selectActiveUser);
  const activePublicUser = useAppSelector(selectActivePublicUser);

  const resetChatImageUpload = useCallback(() => {
    dispatch(chatImageUploadErrorChanged(null));
    dispatch(
      chatImageUploadDataUrlChanged({
        chatImageUploadDataUrl: null,
        chatImageUploadDataUrlDimensions: null,
      }),
    );
    dispatch(chatImageUploadIdChanged(null));
    dispatch(chatImageUploadProgressChanged(0));
  }, [dispatch]);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(e => {
    if (!ref.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!conversationId) return;

      resetChatImageUpload();
      if (e.target.files && e.target.files.length) {
        const file = e.target.files[0];
        if (file.size > MAX_IMG_SIZE) {
          dispatch(chatImageUploadErrorChanged('Selected image is too large'));
          return;
        }

        // 1. Create a dataUrl to display optimistically
        const dataUrl = URL.createObjectURL(file);

        // 2. Get dimensions too
        try {
          await new Promise<void>((res, rej) => {
            const img = new Image();
            img.onload = () => {
              dispatch(
                chatImageUploadDataUrlChanged({
                  chatImageUploadDataUrl: dataUrl,
                  chatImageUploadDataUrlDimensions: {
                    w: img.naturalWidth,
                    h: img.naturalHeight,
                  },
                }),
              );
              res();
            };
            img.onerror = rej;
            img.src = dataUrl;
          });
        } catch (error) {
          dispatch(chatImageUploadErrorChanged(`File selected is not a valid image`));
          return;
        }

        // 3. Register the image with backend
        const registeredImg = unwrapResult(await dispatch(registerImage(file.name)));
        dispatch(chatImageUploadIdChanged(registeredImg.id));

        // 4. Send an image chat message with the registered id
        if (mode === 'session' && typeof conversationId === 'number') {
          await dispatch(
            sendChatMessage({
              visitorId,
              content: { blocks: [{ type: ChatMessageBlockType.TEXT, text: '' }] },
              conversationId,
              registeredImg,
              file,
            }),
          );
        } else if (mode === 'salesfloor' && typeof conversationId === 'string') {
          if (activeUser || activePublicUser) {
            const sender = activeUser
              ? {
                  id: `user_${activeUser?.id}`,
                  displayName: activeUser?.displayName || '',
                  avatarUrl: activeUser?.avatar ? activeUser.avatar?.imageUrl : null,
                  lastActivity: null,
                  status: 'active',
                }
              : {
                  id: `public_user_${activePublicUser?.id}`,
                  displayName: activePublicUser?.displayName || '',
                  avatarUrl: activePublicUser?.avatar
                    ? activePublicUser.avatar?.imageUrl
                    : null,
                  lastActivity: null,
                  status: 'active',
                };
            const timestamp = Date.now();
            const message: SalesfloorChatContentMessage = {
              type: SalesfloorChatMessageType.CONTENT,
              id: `${conversationId}-${visitorId}-${timestamp}`,
              timestamp: timestamp,
              content: {
                blocks: [
                  {
                    type: ChatMessageBlockType.TEXT,
                    text: '',
                  },
                ],
              },
              sender,
              conversationId: conversationId,
              imageIds: [registeredImg.id],
              images: dataUrl
                ? [
                    {
                      id: registeredImg.id,
                      signedDownloadUrl: dataUrl,
                      filename: file.name,
                      convertStatus: 'done',
                      proxies: {},
                    },
                  ]
                : [],
            };
            const messageArgs: SalesfloorChatMessageArgs = {
              registeredImg,
              file,
            };
            await dispatch(addConversationMessage({ message, messageArgs }));
          }
        }

        // 5. Reset after a delay
        setTimeout(resetChatImageUpload, 2000);
      }
    },
    [
      activeUser,
      activePublicUser,
      conversationId,
      dispatch,
      mode,
      resetChatImageUpload,
      visitorId,
    ],
  );

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files);
      if (files && files.length) {
        handleImageChange(({
          target: { files },
        } as unknown) as ChangeEvent<HTMLInputElement>);
      }
    },
    [handleImageChange],
  );

  return [
    ref,
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleImageChange,
  ];
};
